import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { accent } from '@/components/feature/solution/theme';

interface ModuleItem {
  icon: string;
  title: string;
  desc: string;
}

interface LoopStep {
  icon: string;
  label: string;
}

interface AgriSoftwareModulesProps {
  data: {
    eyebrow: string;
    title: string;
    subtitle: string;
    items: ModuleItem[];
    loop: {
      title: string;
      desc: string;
      steps: LoopStep[];
    };
  };
}

export default function AgriSoftwareModules({ data }: AgriSoftwareModulesProps) {
  const a = accent('green');

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50">
      <div className="max-w-[1200px] mx-auto">
        <SectionHeading eyebrow={data.eyebrow} title={data.title} subtitle={data.subtitle} />

        <Reveal className="mt-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.items.map((item) => (
              <div
                key={item.title}
                className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200 transition-all duration-300 hover:-translate-y-1 hover:border-accent-200"
              >
                <span className={`w-11 h-11 flex items-center justify-center rounded-lg ${a.iconBox}`}>
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading font-bold text-foreground-950 text-base leading-snug">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal className="mt-8" delay={100}>
          <div className="rounded-2xl bg-foreground-950 p-6 md:p-8">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/20 text-accent-300">
                <i className="ri-loop-right-line text-xl"></i>
              </span>
              <h3 className="font-heading font-bold text-background-50 text-lg">{data.loop.title}</h3>
            </div>

            <p className="mt-4 text-sm text-foreground-300 leading-relaxed max-w-3xl">{data.loop.desc}</p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {data.loop.steps.map((step, idx) => (
                <div key={step.label} className="flex items-center gap-3">
                  <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-background-50/10 border border-background-50/15">
                    <i className={`${step.icon} text-base text-accent-300`}></i>
                    <span className="text-sm text-background-100 whitespace-nowrap">{step.label}</span>
                  </div>
                  {idx < data.loop.steps.length - 1 && (
                    <i className="ri-arrow-right-line text-accent-400"></i>
                  )}
                </div>
              ))}
              <i className="ri-loop-right-line text-accent-400 hidden sm:inline"></i>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}